// React Core
import type React from 'react';
import { useState } from 'react';
// Assets
import IconDelete from '@/assets/icon/icon-delete-circle.svg';
import IconSpeaker from '@/assets/icon/icon-speaker-modal.svg';
import { cn } from '@/lib/utils';
// Components
import { Button } from '@/shared/components/ui/Button';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
// Config / Constants
import { URLS } from '@/shared/config/urls';
// Hooks
import useCommonCodes from '@/shared/hooks/useCommonCodes';

// Types
interface Option {
  value: string;
  label: string;
}

interface SpeakerScheduleItem {
  id?: string;
  is_new?: boolean;
  volume: number;
  start_hour: number;
  start_minute: number;
  end_hour: number;
  end_minute: number;
}

interface SpeakerInputForm {
  speaker_file?: string;
  speaker_schedule?: SpeakerScheduleItem[];
}

interface FormDataSpeakerProps {
  onDelete: (index: number) => void;
  data: SpeakerScheduleItem;
  index: number;
  onChangeForm: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    index: number
  ) => void;
}

interface ModalSpeakerOperationProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onChangeForm: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    index: number
  ) => void;
  speakerInputForm: SpeakerInputForm;
  setSpeakerInputForm: React.Dispatch<React.SetStateAction<SpeakerInputForm>>;
  speakerScheduleInputFormList: SpeakerScheduleItem[];
  setSpeakerScheduleInputFormList: React.Dispatch<React.SetStateAction<SpeakerScheduleItem[]>>;
}

const FormDataSpeaker: React.FC<FormDataSpeakerProps> = ({
  onDelete,
  data,
  index,
  onChangeForm,
}) => {
  const volumeOptions: Option[] = Array.from({ length: 10 }, (_, index) => {
    const value = (index + 1) * 10; // 10, 20, 30, ..., 100
    return { value: `${value}`, label: `${value}%` };
  });

  // 00 시부터 23 시까지의 시간 옵션 생성
  const hoursOptions: Option[] = Array.from({ length: 24 }, (_, index) => ({
    value: String(index), // 문자열로 변환
    label: `${index.toString().padStart(2, '0')} 시`,
  }));

  // 00 분부터 59 분까지의 분 옵션 생성
  const minOptions: Option[] = Array.from({ length: 60 }, (_, index) => ({
    value: String(index), // 문자열로 변환
    label: `${index.toString().padStart(2, '0')} 분`,
  }));

  // [{"volume": 50, "start_hour": 0, "start_minute": 0, "end_hour": 18, "end_minute": 59}]
  const { volume, start_hour, start_minute, end_hour, end_minute } = data;

  return (
    <div className="box-volume-schedule flex flex-row items-center w-full gap-[15px]">
      <Button
        variant="ghost"
        type="button"
        className="p-0 h-auto hover:bg-transparent"
        onClick={() => onDelete(index)}
      >
        <img src={IconDelete} alt="" />
      </Button>
      <div className="grid grid-cols-4 items-center gap-[15px] w-full">
        <div className="w-full flex">
          <DetailForm
            inputType="select"
            className="items-center!"
            label="음량"
            required={true}
            styleLabel="w-[100px]!"
            name="volume"
            value={volume} // 기본값 50%로 설정
            onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
            optionSelect={volumeOptions}
          />
        </div>
        <div className="w-full grid col-span-3">
          <DetailForm
            inputType="select"
            className="items-center!"
            label="적용 시간"
            showInput={false}
            required={true}
            onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
            styleLabel="w-[100px]!"
          >
            <div className="w-full flex gap-[8px] items-center">
              <DetailForm
                inputType="select"
                className="items-center!"
                label="시작시간"
                name="start_hour"
                value={start_hour} //
                required={true}
                showTitle={false}
                onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
                optionSelect={hoursOptions}
              />
              <DetailForm
                inputType="select"
                className="items-center!"
                label="시작분"
                name="start_minute"
                value={start_minute} //
                required={true}
                showTitle={false}
                onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
                optionSelect={minOptions}
              />
              <span>~</span>
              <DetailForm
                inputType="select"
                className="items-center!"
                label="끝시간"
                name="end_hour"
                value={end_hour} //
                required={true}
                showTitle={false}
                onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
                optionSelect={hoursOptions}
              />
              <DetailForm
                inputType="select"
                className="items-center!"
                label="끝분"
                name="end_minute"
                value={end_minute} //
                required={true}
                showTitle={false}
                onChange={(e) => onChangeForm(e, index)} // 선택 시 상태 변경
                optionSelect={minOptions}
              />
            </div>
          </DetailForm>
        </div>
      </div>
    </div>
  );
};

const ModalSpeakerOperation: React.FC<ModalSpeakerOperationProps> = ({
  isOpen,
  onClose,
  onSave,
  onChangeForm,
  speakerInputForm,
  setSpeakerInputForm,
  speakerScheduleInputFormList,
  setSpeakerScheduleInputFormList,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const [optionParams, _setOptionParams] = useState<string>('upper-code=SPK_FL');
  const { commonListData } = useCommonCodes({ optionParams });


  // SPK_FL을 기준으로 value와 name을 매핑하기
  const speakerFileTypeOptions: Option[] =
    commonListData?.SPK_FL?.code?.map((value: string | null, index: number) => ({
      value: value ?? '',
      label: commonListData?.SPK_FL?.name[index] ?? '',
    })) || [];


  // [{"volume": 50, "start_hour": 0, "start_minute": 0, "end_hour": 18, "end_minute": 59}]

  const handleAddForm = (): void => {
    //setSpeakerForms((prev) => [...prev, {}]);
    const defaultForm: SpeakerScheduleItem = {
      id: `speaker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      is_new: true,
      volume: 50,
      start_hour: 0,
      start_minute: 0,
      end_hour: 18,
      end_minute: 59,
    };

    // 1. 새 리스트 생성
    const newScheduleList = [...speakerScheduleInputFormList, defaultForm];

    // 2. 상태 업데이트
    setSpeakerScheduleInputFormList(newScheduleList);

    // 3. speakerInputForm에 반영
    setSpeakerInputForm((prev) => ({
      ...prev,
      speaker_schedule: newScheduleList,
    }));
  };

  const handleDeleteForm = (indexToRemove: number): void => {
    //alert('동적박스 삭제')
    //setSpeakerForms((prev) => prev.filter((_, index) => index !== indexToRemove));
    setSpeakerScheduleInputFormList((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleClickSpeakerIcon = async (): Promise<void> => {
    //alert('음악실행!')
    const speaker_file = speakerInputForm.speaker_file;
    //alert(speaker_file)

    if (!speaker_file) {
      //alert("파일명이 없습니다.");
      return;
    }

    try {
      setIsPlaying(true); // 🎵 재생 시작 상태

      const encodedUrl = `data/speaker/${speaker_file}.wav`;
      const fullUrl = `${URLS.BACK_DSH}/common/audio/${encodedUrl}?t=${Date.now()}`;
      console.log(fullUrl);

      const response = await fetch(fullUrl, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('오디오 요청 실패');
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);

      audio.play();

      // 재생이 끝나면 다시 활성화
      audio.onended = () => {
        setIsPlaying(false);
      };

      console.log('📦 Audio fetched and played successfully');
    } catch (error) {
      console.error('오디오 재생 실패:', error);
      alert('오디오 재생 중 오류가 발생했습니다.');
      setIsPlaying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 w-full h-full bg-black/50 z-1000 py-[65px] px-[10%]">
      <div className="w-full h-full flex flex-col gap-[25px] p-[40px] bg-db-card border border-[#D6DBE1] rounded-[5px]">
        <span className="h3bold">스피커 동작 설정</span>

        <div className="w-full grid grid-cols-3 gap-x-[30px] gap-y-[15px]">
          <DetailForm
            inputType="select"
            className="items-center!"
            label="음성 파일 옵션"
            name="speaker_file"
            onChange={(e) => onChangeForm(e, -1)} // 선택 시 상태 변경
            required={true}
            styleLabel="w-[120px]!"
            value={speakerInputForm?.speaker_file}
            optionSelect={speakerFileTypeOptions}
          >
            <Button
              variant="ghost"
              type="button"
              className="p-0 h-auto hover:bg-transparent"
              onClick={!isPlaying ? handleClickSpeakerIcon : undefined}
            >
              <img
                src={IconSpeaker}
                alt=""
                className={cn(
                  'cursor-pointer transition-opacity duration-200',
                  isPlaying && 'opacity-40 pointer-events-none'
                )}
              />
            </Button>
          </DetailForm>
        </div>

        <div className="w-full flex flex-col gap-[15px] p-[25px] overflow-auto h-[calc(100%-50px)] border border-[#B0B0B0] ">
          <DetailForm
            className="items-center!"
            label="음량 스케줄 설정"
            styleLabel="w-[125px]!"
            required={true}
            showInput={false}
            disabled={true}
          >
            <Button
              variant="default"
              type="button"
              onClick={handleAddForm}
              className="body1bold px-[20px] min-w-[86px] text-center py-[3px] text-[#FEFEFE] bg-[#3D6B85] hover:bg-[#3D6B85] hover:opacity-80 cursor-pointer"
            >
              <span>Add +</span>
            </Button>
          </DetailForm>

          {speakerScheduleInputFormList?.map((data, index) => (
            <FormDataSpeaker
              key={data.id || `speaker-${index}`}
              data={data}
              index={index}
              onDelete={handleDeleteForm}
              onChangeForm={onChangeForm}
            />
          ))}
        </div>

        <div className="flex flex-row justify-center gap-[7px]">
          <Button customButton="btn-search" label="저장" onClick={() => onSave()} />
          <Button customButton="btn-danger" label="취소" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ModalSpeakerOperation;
